import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RootContext from '../services/context-states/root-context';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import {
  exampleMenu,
  exampleWatch,
  exampleRatings,
  exampleResults,
  iconLightBulb,
  iconHammerWrench,
} from '../assets';

import { enableVideo } from '../helpers/entitlements';
import AppTour from 'types/app-tour';
import { registeredTrademark } from '../helpers/strings';
import { NextSurveyResponse } from '../types/api-client-types';
import { callApi, isNotFoundError } from '../api/helpers';
import { userApi } from '../api/openapi-axios-client';
import { logAnalyticsEvent } from '../helpers/analytics';
import { GoogleAnalyticsEvent } from '../types/analytic-events';

interface SetupModalStep {
  image: string;
  caption: string;
}

interface CardProps {
  icon: string;
  title: string;
  description: string;
  modal: () => void;
}

const Home: React.FC = () => {
  const { appTourContext, billingContext, userContext } = useContext(RootContext);
  const { billingInformation } = billingContext;
  const { currentUser } = userContext;
  const { appTour, setTourState } = appTourContext;

  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false);
  const [setupStep, setSetupStep] = useState(0);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const displayWelcomeModal = searchParams.get('welcome') === 'true';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  useEffect(() => {
    if (displayWelcomeModal) {
      setShowWelcomeModal(true);
    }
  }, [displayWelcomeModal]);

  useEffect(() => {
    if (appTour.tourActive) {
      // Small delay to ensure DOM elements are ready
      setTimeout(() => {
        setTourState((prevState: AppTour) => ({
            ...prevState,
            run: true,
        }));
      }, 500);
    }
  }, [appTour.tourActive, setTourState]);

  useEffect(() => {
    const checkNextSurvey = async () => {
      if (!currentUser) {
        return;
      }

      try {
        const nextSurvey = await callApi<NextSurveyResponse>(() => userApi.userSurveys.getNextSurvey());

        if (nextSurvey?.surveyKey) {
          navigate(`/survey/${nextSurvey.surveyKey}?returnTo=/home`, { replace: true });
        }
      } catch (error) {
        // A 404 means there is no survey pending — not an error, just continue.
        if (!isNotFoundError(error)) {
          console.error('Error checking for next survey:', error);
        }
      }
    }

    checkNextSurvey();
  }, [currentUser, navigate]);

  const buildCard = ({ icon, title, description, modal }: CardProps) => {
    return (
      <Box sx={{
        boxShadow: 3,
        borderRadius: 2,
        padding: { xs: '12px 8px', sm: '8px 4px' },
        backgroundColor: 'container.background',
        borderColor: 'container.border',
        borderWidth: 1,
        width: { xs: '300px', sm: '300px', md: '300px' },
        textAlign: 'center',
        minHeight: { xs: 'auto', sm: '250px' }
      }}>
        <Stack spacing={2} sx={{ alignContent: 'center', alignItems: 'center', height: '100%' }}>
          <img src={icon} alt={`Icon for ${title}`} style={{ width: 50, height: 50, marginTop: 10, marginBottom: 10 }} />
          <Typography sx={{
            fontWeight: 'bold',
            fontSize: { xs: 20, sm: 22 },
            marginTop: 2,
            color: 'text.primary'
          }}>{title}</Typography>
          <Typography sx={{
            fontSize: { xs: 16, sm: 18 },
            marginTop: 2,
            color: 'text.primary',
            px: { xs: 2, sm: 1 }
          }}>{description}</Typography>
          <IconButton onClick={modal} sx={{ mt: 'auto' }}>
            <ArrowCircleRightOutlinedIcon sx={{ width: { xs: 40, sm: 50 }, height: { xs: 40, sm: 50 }}} />
          </IconButton>
        </Stack>
      </Box>
    );
  };

  const handleSetupModalClose = () => {
    setShowSetupModal(false);
    setSetupStep(0);
  };

  const handleStepMove = (direction: 'forward' | 'back') => {
    if (direction === 'forward') {
      if (setupStep < setupModalData.length - 1) {
        setSetupStep(setupStep + 1);
      }
    } else {
      if (setupStep > 0) {
        setSetupStep(setupStep - 1);
      }
    }
  };

  const setupSteps = () => {
    return (
      <div style={{ display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)' }}>
        <div style={{ maxHeight: '350px', minHeight: '350px', display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)', marginTop: 8 }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-start', alignContent: 'center', justifyContent: 'center', paddingTop: 2, textAlign: 'center' }}>
            <img src={setupModalData[setupStep].image} alt={setupModalData[setupStep].caption} />
          </Box>
        </div>
        <div style={{ maxHeight: '160px', minHeight: '160px', display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr)', backgroundColor: 'container.background', padding: '0 2px' }}>
          <Box sx={{ display: 'flex', height: '160px', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', alignContent: 'center', justifyContent: 'center' }}>
              <IconButton
                onClick={() => handleStepMove('back')}
                sx={{ color: (theme) => theme.palette.grey[500], visibility: setupStep > 0 ? 'visible' : 'hidden' }}>
                <KeyboardArrowLeft sx={{ fontSize: '50px' }} />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', padding: 2 }}>
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-start', alignContent: 'center', justifyContent: 'center', paddingTop: 2, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: '#000000' }}>{setupModalData[setupStep].caption}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', alignContent: 'center', justifyContent: 'center', paddingBottom: 1 }}>
                {setupStep + 1 === setupModalData.length ? (
                  <Button variant="contained" onClick={() => navigate('/videos/watch')} disabled={!enableVideo(currentUser, billingInformation)}>
                    Start watching videos
                  </Button>
                ) : (
                  <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: 'lightgray' }}>
                    {setupStep + 1} / {setupModalData.length}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', alignContent: 'center', justifyContent: 'center' }}>
              <IconButton
                onClick={() => handleStepMove('forward')}
                sx={{ color: (theme) => theme.palette.grey[500], visibility: setupStep + 1 < setupModalData.length ? 'visible' : 'hidden' }}
              >
                <KeyboardArrowRight sx={{ fontSize: '50px' }} />
              </IconButton>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  const setupModalData: SetupModalStep[] = [
    {
      image: exampleMenu,
      caption: 'Navigate to the Watch Page under the Videos tab.',
    },
    {
      image: exampleWatch,
      caption: 'Watch videos to learn about different careers and give a Yes or No reaction. Each reaction you give contributes to a more accurate career recommendation.',
    },
    {
      image: exampleRatings,
      caption: 'When you\'re done with the video, you can proceed to the next video.',
    },
    {
      image: exampleResults,
      caption: 'After watching 30 videos, you\'ll be prompted to go to your Results Page to see your career insights.',
    },
  ];

  const welcomeModal = () : React.ReactElement => {
    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 20, paddingBottom: 20, paddingLeft: 40, paddingRight: 40, gap: 20}}>
        <Typography sx={{fontSize: 48}}>{String.fromCodePoint(0x1F389)}</Typography>
        <Typography sx={{fontSize: 24, color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main, fontWeight: 900}}>
          We're glad you're here!
        </Typography>
        <div style={{display: 'flex', flexDirection: 'row', gap: 2}}>
          <Typography sx={{fontSize: 18, color: theme.palette.text.primary}}>
            Start exploring your career options with CareerAtlas!
          </Typography>
          <Typography sx={{fontSize: 12, color: theme.palette.text.primary}}>
            {registeredTrademark}
          </Typography>
        </div>
        <Typography sx={{fontSize: 18, color: theme.palette.text.primary}}>
          Let us show you around.
        </Typography>
        <div style={{display: 'flex', flexDirection: 'row', gap: 40, marginTop: 20, height: 50}}>
          <Button variant='outlined' color={`${theme.palette.mode === 'dark' ? 'secondary' : 'primary'}`} type='submit' style={{width: 175}} onClick={() => {
            
            logAnalyticsEvent({
              category: 'tour',
              action: GoogleAnalyticsEvent.UserSkipedWelcomeTour,
              label: 'User skipped Welcome Tour',
            });

            setShowWelcomeModal(false)
            navigate('/home', { replace: true });
          }}>Skip</Button>
          <Button variant='contained' color='primary' type='submit' style={{width: 175}} onClick={() => {
                setTourState((state: AppTour) => ({
                  ...state,
                  tourActive: true,
                  run: true,
                  stepIndex: 0
              }));
              setShowWelcomeModal(false);
              navigate('/home', { replace: true });
          }}>Start the tour</Button>
        </div>
      </div>
    );
  };

  return (
    <Container>
      <Box sx={{ width: '100%' }}>
        <Dialog
          open={showSetupModal}
          onClose={handleSetupModalClose}
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-container': {
              '& .MuiPaper-root': {
                background: '#EEEDFF',
                width: '100%',
                maxWidth: { xs: '100%', sm: '650px' },
                height: { xs: '100%', sm: 'auto' },
                maxHeight: { xs: '100%', sm: '550px' },
                margin: { xs: 0, sm: 2 },
              },
            },
          }}>
          <DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleSetupModalClose}
              sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          {setupSteps()}
        </Dialog>
        <Dialog
          open={showHowItWorksModal}
          onClose={() => setShowHowItWorksModal(false)}
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-container': {
              '& .MuiPaper-root': {
                width: '100%',
                maxWidth: { xs: '100%', sm: '650px' },
                margin: { xs: 0, sm: 2 },
              },
            },
          }}
        >
          <DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => setShowHowItWorksModal(false)}
              sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Stack spacing={2} sx={{ padding: { xs: 2, sm: 4 } }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: { xs: 20, sm: 24 } }}>How your Results are Calculated</Typography>
            <Typography sx={{ fontSize: { xs: 16, sm: 20 } }}>
              The career paths shown to you in your Results page are calculated from your reactions to the videos.
              <br />
              <br />
              Every video you've liked will increase a career path's ranking and every video that you've disliked will decrease a career path's ranking.
              <br />
              <br />
              The selection of curated videos will be refreshed based on your results after you have watched 30 videos.
            </Typography>
          </Stack>
        </Dialog>
        <Dialog open={showWelcomeModal} fullScreen={isMobile}>
          {welcomeModal()}
        </Dialog>
      </Box>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <Typography sx={{
          fontWeight: 'bold',
          fontSize: { xs: 28, sm: 32, md: 38 },
          textAlign: { xs: 'center', sm: 'left' }
        }}>Welcome back, {currentUser?.firstName}!</Typography>
      </Box>
      <Box sx={{ mt: { xs: 3, sm: 4 } }}>
        <Typography sx={{
          fontSize: { xs: 22, sm: 24, md: 28 },
          fontWeight: 'bold',
          marginTop: 2,
          textAlign: { xs: 'center', sm: 'left' },
          mb: { xs: 3, sm: 4 }
        }}>Guides to get started</Typography>
      </Box>
      <Box sx={{ width: '100%' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3, md: 4 }}
          aria-label="Home feature cards list"
          sx={{
            alignItems: { xs: 'center', sm: 'flex-start' },
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
          {buildCard({
            icon: iconLightBulb,
            title: 'Getting Started',
            description: 'Learn how to begin your CareerAtlas journey.',
            modal: () => setShowSetupModal(true),
          })}
          {buildCard({
            icon: iconHammerWrench,
            title: 'How It Works',
            description: 'Find out how we calculate your results.',
            modal: () => setShowHowItWorksModal(true),
          })}
        </Stack>
      </Box>
    </Container>
  );
};

export default Home; 